import moment from "moment";
import { mapState } from "vuex";
import { countByKeys } from "@/utils/countByKeys";
import { percentFromNumber } from "@/utils/percentFromNumber";
import { MemberInviteStatuses } from "@/enums/MemberInviteStatuses";
import { FamilyHubAppStatus } from "@/enums/FamilyHubAppStatus";
import Loader from "@/components/loader/Loader.vue";
import { PremiumServiceEventTypeEnum, PremiumServiceEventTypes, PremiumServiceProductTypeEnum, PremiumServiceRequestTypeEnum } from "@/enums/PremiumServiceEventTypes";
import { calculateServicesSpentMoney } from "@/utils/calculateServicesSpentMoney";

export default {
  name: 'ReportFamily',
  components: { Loader },
  data() {
    return {
      loading: true,
      loadingLikes: true,
      eventTypes: Object.values(PremiumServiceEventTypeEnum),
      requestTypes: Object.values(PremiumServiceRequestTypeEnum),
      productTypes: Object.values(PremiumServiceProductTypeEnum),
      statusColor: {
        [MemberInviteStatuses.CreatedWithoutInvitation]: '#F44336',
        [MemberInviteStatuses.InvitationAccepted]: '#4CAF50',
        [MemberInviteStatuses.InvitationSent]: '#FF9700'
      },
      defaultHeader: [
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Name"],
          value: "name",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["ReportFamily.BookingsAndOrders"],
          value: "servicesCount",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Type"],
          value: "serviceItemType",
        }
      ],
      servicesHeaders: [
        { text: this.$t('GeneralTranslations.Name'), align: "start", value: "name", width: 180 },
        { text: this.$t('GeneralTranslations.Type'), value: "serviceItemType", width: 180 },
        { text: this.$t('GeneralTranslations.Resident'), value: "residentName", width: 180 },
        { text: this.$t('ReportFamily.BookingsAndOrders'), value: "bookedCount", width: 180 },
        { text: this.$t('ReportFamily.TotalSpent'), value: "spentMoney", width: 180 },
        { text: this.$t('ReportFamily.SpentAverage'), value: "spentAverage", width: 180 },
        { text: this.$t('GeneralTranslations.Provider'), value: "providerName", width: 180 },
        { text: this.$t('GeneralTranslations.Dimension'), value: "category", width: 180 },
      ],
      services: []
    }
  },
  async mounted() {
    await this.loadReportData()
  },
  computed: {
    ...mapState({
      familyMemberInvitations: state => (state as any).residentModule.familyMemberInvitations
    }),

    familyMembers() {
      return this.$store.state.residentModule.familyMembers;
    },

    familyMembersAppActivated() {
      return this.$store.state.residentModule.familyMembers.filter(x => x.appStatus === FamilyHubAppStatus.Activated);
    },

    residents() {
      return this.$store.state.residentModule.residentsShortData
    },

    familyLikes(): any[] {
      return this.$store.state.likesModule.familyLikes;
    },

    familyServiceRequests() {
      const services = this.$store.state.additionalServicesModule.serviceRequests.filter(x => x.bookByFamilyId?.length > 0)

      return services
    },

    spentMoney() {
      const spentMoney = this.calculateSpentMoney(this.familyServiceRequests)
      return spentMoney;
    },

    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    },

    servicesPopularity() {
      const serviceCountMap = {};
      this.familyServiceRequests.forEach(service => {
        if (serviceCountMap[service.serviceName]) {
          serviceCountMap[service.serviceName].servicesCount++;
        } else {
          serviceCountMap[service.serviceName] = {
            name: service.serviceName,
            serviceItemType: service?.serviceItemType || '',
            servicesCount: 1
          };
        }
      });
      const groupedServices = Object.values(serviceCountMap)
      const mostPopularServices = [...groupedServices].sort((a: any, b: any) => b.servicesCount - a.servicesCount)
      const leastPopularServices = [...groupedServices].sort((a: any, b: any) => a.servicesCount - b.servicesCount)

      return {
        mostPopularServices: mostPopularServices,
        leastPopularServices: leastPopularServices
      }
    },

    leastPopularServices() {
      const mostPopularServices = this.mostPopularServices
      return mostPopularServices.sort((a: any, b: any) => a.servicesCount - b.servicesCount);
    },

    conciergeEvents() {
      return this.familyServiceRequests.filter((x: any) => this.eventTypes.includes(x.serviceItemType))
    },

    conciergeRequests() {
      return this.familyServiceRequests.filter((x: any) => this.requestTypes.includes(x.serviceItemType))
    },

    conciergeProducts() {
      return this.familyServiceRequests.filter((x: any) => this.productTypes.includes(x.serviceItemType))
    },

    residentsWithFamilyActiveOnApp() {
      const familyMembersAppActivate = this.familyMembersAppActivated;

      if (!familyMembersAppActivate) return []

      const allResidents = this.residents

      const activatedResidentsIds = familyMembersAppActivate.reduce((acc, familyMember) => {
        const residentIdsInShortData = familyMember.residentIds.filter(residentId => allResidents.some(resident => resident.id === residentId));

        acc.push(...residentIdsInShortData);
        return acc;
      }, []);

      const residentsWithFamily = allResidents.filter(resident => activatedResidentsIds.includes(resident.id));

      return residentsWithFamily;
    },

    memberInviteStatuses() {
      return MemberInviteStatuses
    },

    percentOfAppActivatedFamilyMembers() {
      const percent = percentFromNumber(
        this.familyMembers.length,
        this.familyMembersAppActivated.length
      );

      return isNaN(percent) ? 0 : percent.toFixed(0);
    },

    percentOfResidentsWithFamilyActiveOnApp() {
      const percent = percentFromNumber(
        this.residents.length,
        this.residentsWithFamilyActiveOnApp.length
      );

      return isNaN(percent) ? 0 : percent.toFixed(0);
    },

    servicesByStatus() {
      return countByKeys(this.familyMemberInvitations, "status");
    },

    invitationsByStatus() {
      return this.familyMemberInvitations.reduce((result, current) => {

        const status = current["status"];
        const date = moment(current.invitationDateTime).format("DD/MM");
        const findedDate = result.find(invite => invite.name === status);

        if (findedDate) {
          findedDate.data[date] = findedDate.data[date] ? findedDate.data[date] + 1 : 1;
        } else {
          result.push({ name: status, data: { [date]: 1 }, color: this.statusColor[status] });
        }

        return result;
      }, []);
    }
  },
  methods: {
    calculateSpentMoney(serviceRequests) {
      return calculateServicesSpentMoney(serviceRequests);
    },
    calculateFontSize() {
      const textLength = this.spentMoney.toString().length;
      const baseFontSize = 4.2;
      const d = textLength > 10 ? 4 : 3.3
      const calculatedFontSize = baseFontSize - (textLength / d);
      return `${calculatedFontSize}vw`;
    },
    calculateServicePercentByType(name) {
      const percent = percentFromNumber(
        this.familyMemberInvitations.length,
        this.servicesByStatus[name]
      );

      return isNaN(percent) ? 0 : percent.toFixed(2);
    },
    async loadReportData() {
      this.loading = true;
      await this.$store.dispatch("getAdditionalServices")
      await this.$store.dispatch("getServiceRequests")
      await this.$store.dispatch("getFamilyMembers")
      this.loading = false;

      this.services = this.mapServices()

      this.loadingLikes = true
      await this.$store.dispatch("getFamilyLikes", {
        fromDate: moment().add(-1, "years").toDate(),
        toDate: moment().add(1, "months").toDate()
      })
      this.loadingLikes = false
    },
    mapServices() {
      const serviceResidentItemsMap = new Map();

      this.$store.state.additionalServicesModule.additionalServices.forEach((s) => {
        const service = { ...s };

        if (this.eventTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Event;
        } else if (this.requestTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Request;
        } else if (this.productTypes.includes(s.type)) {
          service.serviceItemType = PremiumServiceEventTypes.Product;
        }

        this.residents.forEach((resident) => {
          const residentRequests = this.familyServiceRequests.filter((x) => x.serviceId === service.id && x.residentId === resident.id);

          let serviceResidentItem = serviceResidentItemsMap.get(`${service.id}-${resident.id}`);

          if (!serviceResidentItem) {
            const spentMoney = this.calculateSpentMoney(residentRequests)
            serviceResidentItem = {
              serviceId: service.id,
              residentId: resident.id,
              residentName: resident.name,
              bookedCount: residentRequests.length,
              spentMoney: `${this.facilityCurrency} ${spentMoney}`,
              spentAverage: `${this.facilityCurrency} ${residentRequests.length > 0 ? spentMoney / residentRequests.length : 0}`,
              ...service,
            }

            serviceResidentItemsMap.set(`${service.id}-${resident.id}`, serviceResidentItem);
          }

        });
      });

      const serviceResidentItems = Array.from(serviceResidentItemsMap.values()).sort((a: any, b: any) => b.bookedCount - a.bookedCount)

      return serviceResidentItems;
    }

  }
};
